import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Check, ChevronLeft, ChevronRight, AlertTriangle, FileUp } from 'lucide-react';
import {
  TID, HOST_STEPS, PROPERTY_TYPES, LOCATIONS, DOCUMENT_TYPES, AMENITIES, EXTRA_AMENITIES,
  EVENT_TIERS, getTierForGuests, toKurus, toLira, formatKurus, HARD_CHECKOUT_DEADLINE,
} from '@yalidavet/shared';
import PhotoDropzone, { type UploadedPhoto } from '@/components/host/PhotoDropzone';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

interface Draft {
  title: string;
  description: string;
  propertyType: string;
  city: string;
  district: string;
  neighborhood: string;
  totalAreaM2: number;
  gardenSizeM2: number;
  indoorAreaM2: number;
  maxCapacity: number;
  bedrooms: number;
  bathrooms: number;
  dailyPriceLira: number;
  weekendPriceLira: number;
  cleaningFeeLira: number;
  minNights: number;
  hasPool: boolean;
  hasGarden: boolean;
  hasSeaView: boolean;
  hasParking: boolean;
  parkingCapacity: number;
  hasWifi: boolean;
  hasGenerator: boolean;
  hasSoundSystem: boolean;
  isAccessible: boolean;
  hasStagingArea: boolean;
  stagingAreaM2: number;
  hasCateringArea: boolean;
  cateringAreaM2: number;
  hasMusicLicence: boolean;
  isResidentialZone: boolean;
  checkinTime: string;
  checkoutDeadline: string;
  noiseCurfewTime: string;
  houseRules: string;
  neighborhoodNotes: string;
  eventTypeIds: string[];
}

const emptyDraft = (): Draft => ({
  title: '', description: '', propertyType: 'yali', city: 'İstanbul', district: '', neighborhood: '',
  totalAreaM2: 0, gardenSizeM2: 0, indoorAreaM2: 0, maxCapacity: 50, bedrooms: 0, bathrooms: 0,
  dailyPriceLira: 0, weekendPriceLira: 0, cleaningFeeLira: 0, minNights: 1,
  hasPool: false, hasGarden: true, hasSeaView: false, hasParking: false, parkingCapacity: 0,
  hasWifi: true, hasGenerator: false, hasSoundSystem: false, isAccessible: false,
  hasStagingArea: false, stagingAreaM2: 0, hasCateringArea: false, cateringAreaM2: 0, hasMusicLicence: false,
  isResidentialZone: true, checkinTime: '14:00', checkoutDeadline: HARD_CHECKOUT_DEADLINE, noiseCurfewTime: '23:00',
  houseRules: '', neighborhoodNotes: '', eventTypeIds: [],
});

export default function CreateListingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [photos, setPhotos] = useState<UploadedPhoto[]>([]);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [documents, setDocuments] = useState<Record<string, string>>({});
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: eventTypes } = useQuery({ queryKey: ['event-types'], queryFn: api.eventTypes });

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => ({ ...d, [key]: value }));

  /* ---------------- Garden/capacity validation ---------------- */
  const capacityTier = useMemo(() => {
    try { return getTierForGuests(Math.min(draft.maxCapacity || 1, 150)); } catch { return null; }
  }, [draft.maxCapacity]);

  const gardenShortfall =
    capacityTier && draft.gardenSizeM2 < capacityTier.minGardenM2 ? capacityTier.minGardenM2 - draft.gardenSizeM2 : 0;

  /* ---------------- Event-type requirements ------------------- */
  const needsStaging = draft.eventTypeIds.some((id) => ['dugun', 'nikah', 'nisan'].includes(id));
  const needsCatering = draft.eventTypeIds.includes('dugun');

  /* ---------------- Per-step validation ----------------------- */
  const stepErrors = useMemo((): string[] => {
    const errs: string[] = [];
    if (step === 1 && photos.length < 1) errs.push('En az 1 fotoğraf yükleyin.');
    if (step === 2) {
      if (draft.title.trim().length < 8) errs.push('Başlık en az 8 karakter olmalı.');
      if (!draft.district) errs.push('İlçe seçin.');
      if (draft.totalAreaM2 <= 0) errs.push('Toplam alanı girin.');
      if (draft.maxCapacity <= 0) errs.push('Maksimum kapasiteyi girin.');
      if (draft.dailyPriceLira <= 0) errs.push('Günlük fiyatı girin.');
      if (draft.weekendPriceLira < draft.dailyPriceLira) errs.push('Hafta sonu fiyatı günlük fiyattan düşük olamaz.');
      if (draft.gardenSizeM2 > 0 && !draft.hasGarden) errs.push('Bahçe alanı girdiniz; "Bahçeli" seçeneğini işaretleyin.');
      if (draft.checkoutDeadline > HARD_CHECKOUT_DEADLINE) {
        errs.push(`Çıkış saati en geç ${HARD_CHECKOUT_DEADLINE} olabilir (gürültü yönetmeliği).`);
      }
    }
    if (step === 3) {
      if (draft.eventTypeIds.length === 0) errs.push('En az bir organizasyon tipi seçin.');
      if (needsStaging && !draft.hasStagingArea) errs.push('Düğün/Nikah/Nişan için sahne-kurulum alanı zorunludur.');
      if (needsCatering && !draft.hasCateringArea) errs.push('Düğün için catering alanı zorunludur.');
    }
    if (step === 4) {
      DOCUMENT_TYPES.filter((d) => d.required).forEach((d) => {
        if (!documents[d.value]) errs.push(`${d.label} yüklenmelidir.`);
      });
    }
    return errs;
  }, [step, photos, draft, documents, needsStaging, needsCatering]);

  /* ---------------- Submit ----------------------- */
  const submit = useMutation({
    mutationFn: () =>
      api.createMansion({
        title: draft.title,
        description: draft.description,
        propertyType: draft.propertyType,
        city: draft.city,
        district: draft.district,
        neighborhood: draft.neighborhood || undefined,
        totalAreaM2: draft.totalAreaM2,
        gardenSizeM2: draft.gardenSizeM2,
        indoorAreaM2: draft.indoorAreaM2,
        maxCapacity: draft.maxCapacity,
        bedrooms: draft.bedrooms,
        bathrooms: draft.bathrooms,
        dailyPriceKurus: toKurus(draft.dailyPriceLira),
        weekendPriceKurus: toKurus(draft.weekendPriceLira),
        cleaningFeeKurus: toKurus(draft.cleaningFeeLira),
        minNights: draft.minNights,
        hasPool: draft.hasPool, hasGarden: draft.hasGarden, hasSeaView: draft.hasSeaView,
        hasParking: draft.hasParking, parkingCapacity: draft.parkingCapacity,
        hasWifi: draft.hasWifi, hasGenerator: draft.hasGenerator,
        hasSoundSystem: draft.hasSoundSystem, isAccessible: draft.isAccessible,
        hasStagingArea: draft.hasStagingArea, stagingAreaM2: draft.stagingAreaM2,
        hasCateringArea: draft.hasCateringArea, cateringAreaM2: draft.cateringAreaM2,
        hasMusicLicence: draft.hasMusicLicence,
        isResidentialZone: draft.isResidentialZone,
        checkinTime: draft.checkinTime,
        checkoutDeadline: draft.checkoutDeadline,
        noiseCurfewTime: draft.noiseCurfewTime,
        houseRules: draft.houseRules || undefined,
        neighborhoodNotes: draft.neighborhoodNotes || undefined,
        eventTypeIds: draft.eventTypeIds,
        photoIds: photos.map((p) => p.id),
      }),
    onSuccess: (res) => {
      setCreatedId(res.id);
      navigate('/ilanlarim?created=1');
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'İlan oluşturulamadı.'),
  });

  const uploadDoc = async (docType: string, file: File) => {
    if (!createdId) {
      // Documents need a listing id — stash locally and upload right after create.
      setDocuments((d) => ({ ...d, [docType]: file.name }));
      return;
    }
    const res = await api.uploadDocument(createdId, docType, file);
    setDocuments((d) => ({ ...d, [docType]: res.fileName }));
  };

  return (
    <div className="yd-container max-w-4xl py-10">
      <h1 className="font-display text-3xl font-bold text-ink-900">Yeni İlan Oluştur</h1>
      <p className="mt-1 text-sm text-ink-500">Mülkünüzü 4 adımda davetlere açın.</p>

      {/* ---------------- Stepper ---------------- */}
      <ol className="mt-8 flex items-center gap-2" data-testid={TID.hostStepper} data-current-step={step}>
        {HOST_STEPS.map((s, i) => {
          const done = step > s.step;
          const active = step === s.step;
          return (
            <li key={s.step} className="flex flex-1 items-center gap-2">
              <button
                type="button"
                onClick={() => s.step < step && setStep(s.step)}
                data-testid={TID.hostStep(s.step)}
                data-state={done ? 'completed' : active ? 'active' : 'upcoming'}
                className={cn(
                  'flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm',
                  active && 'font-semibold text-bosphorus-800',
                  done && 'text-ink-600 hover:bg-ink-50',
                  !active && !done && 'text-ink-400',
                )}
              >
                <span
                  className={cn(
                    'grid h-7 w-7 shrink-0 place-items-center rounded-full border text-xs font-bold',
                    done && 'border-bosphorus-600 bg-bosphorus-600 text-white',
                    active && 'border-bosphorus-600 text-bosphorus-700',
                    !active && !done && 'border-ink-200',
                  )}
                >
                  {done ? <Check className="h-4 w-4" /> : s.step}
                </span>
                <span className="hidden sm:inline">{s.label}</span>
              </button>
              {i < HOST_STEPS.length - 1 && <span className="h-px flex-1 bg-ink-200" aria-hidden />}
            </li>
          );
        })}
      </ol>

      <div className="mt-8 yd-card p-6">
        {/* ================= STEP 1 — Fotoğraflar ================= */}
        {step === 1 && (
          <section className="space-y-4">
            <h2 className="font-semibold text-ink-900">1. Fotoğraflar</h2>
            <PhotoDropzone photos={photos} onChange={setPhotos} />
          </section>
        )}

        {/* ================= STEP 2 — Fiyat ve Detaylar ================= */}
        {step === 2 && (
          <section className="space-y-6">
            <h2 className="font-semibold text-ink-900">2. Fiyat ve Detaylar</h2>

            <label className="block">
              <span className="yd-label">İlan Başlığı</span>
              <input
                className="yd-input"
                value={draft.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder="Boğaz Manzaralı Tarihi Yalı"
                data-testid="host-title"
              />
            </label>

            <label className="block">
              <span className="yd-label">Açıklama</span>
              <textarea
                className="yd-input min-h-28"
                value={draft.description}
                onChange={(e) => set('description', e.target.value)}
                data-testid="host-description"
              />
            </label>

            <div className="grid gap-4 sm:grid-cols-3">
              <label className="block">
                <span className="yd-label">Mülk Tipi</span>
                <select className="yd-input" value={draft.propertyType} onChange={(e) => set('propertyType', e.target.value)} data-testid="host-property-type">
                  {PROPERTY_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="yd-label">İlçe</span>
                <select className="yd-input" value={draft.district} onChange={(e) => set('district', e.target.value)} data-testid="host-district">
                  <option value="">Seçiniz</option>
                  {LOCATIONS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="yd-label">Mahalle</span>
                <input className="yd-input" value={draft.neighborhood} onChange={(e) => set('neighborhood', e.target.value)} data-testid="host-neighborhood" />
              </label>
            </div>

            {/* Prices — explicit daily vs weekend */}
            <div className="grid gap-4 sm:grid-cols-3">
              <label className="block">
                <span className="yd-label">Günlük Fiyat (₺)</span>
                <input
                  type="number" min={0} className="yd-input"
                  value={draft.dailyPriceLira || ''}
                  onChange={(e) => set('dailyPriceLira', Number(e.target.value))}
                  data-testid={TID.hostDailyPrice}
                  data-kurus={toKurus(draft.dailyPriceLira)}
                />
              </label>
              <label className="block">
                <span className="yd-label">Hafta Sonu Fiyat (₺)</span>
                <input
                  type="number" min={0} className="yd-input"
                  value={draft.weekendPriceLira || ''}
                  onChange={(e) => set('weekendPriceLira', Number(e.target.value))}
                  data-testid={TID.hostWeekendPrice}
                  data-kurus={toKurus(draft.weekendPriceLira)}
                />
              </label>
              <label className="block">
                <span className="yd-label">Temizlik Ücreti (₺)</span>
                <input
                  type="number" min={0} className="yd-input"
                  value={draft.cleaningFeeLira || ''}
                  onChange={(e) => set('cleaningFeeLira', Number(e.target.value))}
                  data-testid="host-cleaning-fee"
                />
              </label>
            </div>

            {/* Area & capacity — drives the matching engine */}
            <div className="grid gap-4 sm:grid-cols-4">
              <label className="block">
                <span className="yd-label">Toplam Alan (m²)</span>
                <input type="number" min={0} className="yd-input" value={draft.totalAreaM2 || ''} onChange={(e) => set('totalAreaM2', Number(e.target.value))} data-testid="host-total-area" />
              </label>
              <label className="block">
                <span className="yd-label">Bahçe Alanı (m²)</span>
                <input type="number" min={0} className="yd-input" value={draft.gardenSizeM2 || ''} onChange={(e) => set('gardenSizeM2', Number(e.target.value))} data-testid={TID.hostGardenSize} />
              </label>
              <label className="block">
                <span className="yd-label">Kapalı Alan (m²)</span>
                <input type="number" min={0} className="yd-input" value={draft.indoorAreaM2 || ''} onChange={(e) => set('indoorAreaM2', Number(e.target.value))} data-testid="host-indoor-area" />
              </label>
              <label className="block">
                <span className="yd-label">Maks. Kapasite</span>
                <input type="number" min={1} max={150} className="yd-input" value={draft.maxCapacity || ''} onChange={(e) => set('maxCapacity', Number(e.target.value))} data-testid={TID.hostCapacity} />
              </label>
            </div>

            {/* Live garden-size feedback */}
            {capacityTier && (
              <p
                className={cn(
                  'flex gap-2 rounded-lg p-3 text-sm',
                  gardenShortfall > 0 ? 'bg-amber-50 text-amber-900' : 'bg-emerald-50 text-emerald-900',
                )}
                data-testid={TID.hostGardenWarning}
                data-shortfall={gardenShortfall}
                data-tier={capacityTier.tier}
              >
                <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
                {gardenShortfall > 0 ? (
                  <span>
                    {draft.maxCapacity} kişilik kapasite Tier {capacityTier.tier} kuralına girer ve
                    en az <strong>{capacityTier.minGardenM2} m²</strong> bahçe gerektirir.
                    Şu an <strong>{gardenShortfall} m²</strong> eksiğiniz var — bu kapasitedeki aramalarda görünmezsiniz.
                  </span>
                ) : (
                  <span>
                    Bahçeniz Tier {capacityTier.tier} ({capacityTier.minGardenM2} m²) şartını karşılıyor.
                    {draft.maxCapacity} kişiye kadar davetlerde listelenirsiniz.
                  </span>
                )}
              </p>
            )}

            <div className="grid gap-4 sm:grid-cols-4">
              <label className="block">
                <span className="yd-label">Yatak Odası</span>
                <input type="number" min={0} className="yd-input" value={draft.bedrooms || ''} onChange={(e) => set('bedrooms', Number(e.target.value))} data-testid="host-bedrooms" />
              </label>
              <label className="block">
                <span className="yd-label">Banyo</span>
                <input type="number" min={0} className="yd-input" value={draft.bathrooms || ''} onChange={(e) => set('bathrooms', Number(e.target.value))} data-testid="host-bathrooms" />
              </label>
              <label className="block">
                <span className="yd-label">Min. Gece</span>
                <input type="number" min={1} className="yd-input" value={draft.minNights} onChange={(e) => set('minNights', Number(e.target.value))} data-testid="host-min-nights" />
              </label>
              <label className="block">
                <span className="yd-label">Otopark Kapasitesi</span>
                <input type="number" min={0} className="yd-input" value={draft.parkingCapacity || ''} onChange={(e) => set('parkingCapacity', Number(e.target.value))} data-testid="host-parking-capacity" />
              </label>
            </div>

            {/* Amenities */}
            <fieldset>
              <legend className="yd-label">Özellikler</legend>
              <div className="flex flex-wrap gap-2">
                {[...AMENITIES, ...EXTRA_AMENITIES].map((a) => {
                  const key = a.key as keyof Draft;
                  const active = draft[key] as boolean;
                  return (
                    <button
                      key={a.key}
                      type="button"
                      onClick={() => set(key, !active as any)}
                      aria-pressed={active}
                      className={cn('yd-pill', active && 'yd-pill-active')}
                      data-testid={`host-amenity-${a.key}`}
                    >
                      {a.label}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            {/* Neighbourhood rules */}
            <fieldset className="grid gap-4 sm:grid-cols-3">
              <legend className="yd-label col-span-full">Komşuluk & Saat Kuralları</legend>
              <label className="block">
                <span className="yd-label">Giriş Saati</span>
                <input type="time" className="yd-input" value={draft.checkinTime} onChange={(e) => set('checkinTime', e.target.value)} data-testid="host-checkin-time" />
              </label>
              <label className="block">
                <span className="yd-label">Kesin Çıkış (maks. {HARD_CHECKOUT_DEADLINE})</span>
                <input type="time" max={HARD_CHECKOUT_DEADLINE} className="yd-input" value={draft.checkoutDeadline} onChange={(e) => set('checkoutDeadline', e.target.value)} data-testid="host-checkout-deadline" />
              </label>
              <label className="block">
                <span className="yd-label">Müzik Bitiş Saati</span>
                <input type="time" className="yd-input" value={draft.noiseCurfewTime} onChange={(e) => set('noiseCurfewTime', e.target.value)} data-testid="host-noise-curfew" />
              </label>
              <label className="col-span-full flex items-center gap-2 text-sm">
                <input type="checkbox" className="h-4 w-4 rounded border-ink-300 text-bosphorus-600" checked={draft.isResidentialZone} onChange={(e) => set('isResidentialZone', e.target.checked)} data-testid="host-residential-zone" />
                Mülk yerleşim bölgesindedir (gürültü uyarısı gösterilir)
              </label>
              <label className="col-span-full block">
                <span className="yd-label">Ev Kuralları</span>
                <textarea className="yd-input min-h-20" value={draft.houseRules} onChange={(e) => set('houseRules', e.target.value)} data-testid="host-house-rules" />
              </label>
            </fieldset>
          </section>
        )}

        {/* ================= STEP 3 — Organizasyon Tipleri ================= */}
        {step === 3 && (
          <section className="space-y-6">
            <h2 className="font-semibold text-ink-900">3. Organizasyon Tipleri</h2>
            <p className="text-sm text-ink-500">Mülkünüzün ev sahipliği yapabileceği davet türlerini seçin.</p>

            <div className="flex flex-wrap gap-2">
              {(eventTypes?.items ?? []).map((e: any) => {
                const active = draft.eventTypeIds.includes(e.id);
                return (
                  <button
                    key={e.id}
                    type="button"
                    aria-pressed={active}
                    onClick={() =>
                      set('eventTypeIds', active
                        ? draft.eventTypeIds.filter((x) => x !== e.id)
                        : [...draft.eventTypeIds, e.id])
                    }
                    className={cn('yd-pill', active && 'yd-pill-active')}
                    data-testid={TID.hostEventToggle(e.id)}
                    data-active={active}
                  >
                    {e.label_tr}
                  </button>
                );
              })}
            </div>

            {/* Auto-flagged facility requirements */}
            {(needsStaging || needsCatering) && (
              <div className="space-y-3 rounded-xl border border-bosphorus-200 bg-bosphorus-50 p-4" data-testid="host-facility-requirements">
                <p className="text-sm font-semibold text-bosphorus-900">
                  Seçtiğiniz organizasyonlar için zorunlu alanlar
                </p>
                {needsStaging && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" className="h-4 w-4 rounded border-ink-300 text-bosphorus-600" checked={draft.hasStagingArea} onChange={(e) => set('hasStagingArea', e.target.checked)} data-testid="host-staging-area" />
                      Sahne / kurulum alanı mevcut
                    </label>
                    <label className="block">
                      <span className="sr-only">Sahne alanı m²</span>
                      <input type="number" min={0} placeholder="Sahne alanı m²" className="yd-input" value={draft.stagingAreaM2 || ''} onChange={(e) => set('stagingAreaM2', Number(e.target.value))} data-testid="host-staging-area-m2" />
                    </label>
                  </div>
                )}
                {needsCatering && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" className="h-4 w-4 rounded border-ink-300 text-bosphorus-600" checked={draft.hasCateringArea} onChange={(e) => set('hasCateringArea', e.target.checked)} data-testid="host-catering-area" />
                      Catering / servis alanı mevcut
                    </label>
                    <label className="block">
                      <span className="sr-only">Catering alanı m²</span>
                      <input type="number" min={0} placeholder="Catering alanı m²" className="yd-input" value={draft.cateringAreaM2 || ''} onChange={(e) => set('cateringAreaM2', Number(e.target.value))} data-testid="host-catering-area-m2" />
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* Tier summary */}
            <div className="rounded-lg bg-ink-50 p-4 text-sm text-ink-600">
              <p className="font-semibold text-ink-800">Bahçenize göre görünürlük</p>
              <ul className="mt-2 space-y-1">
                {EVENT_TIERS.map((t) => {
                  const ok = draft.gardenSizeM2 >= t.minGardenM2 && draft.maxCapacity >= t.minGuests;
                  return (
                    <li key={t.tier} data-testid={`host-tier-eligibility-${t.tier}`} data-eligible={ok}>
                      {ok ? '✅' : '⛔'} Tier {t.tier} ({t.minGuests}–{t.maxGuests} kişi, ≥{t.minGardenM2} m²)
                    </li>
                  );
                })}
              </ul>
            </div>
          </section>
        )}

        {/* ================= STEP 4 — Belge Yükleme ================= */}
        {step === 4 && (
          <section className="space-y-6">
            <h2 className="font-semibold text-ink-900">4. Belge Yükleme</h2>
            <p className="text-sm text-ink-500">
              Belgeler yalnızca doğrulama ekibimiz tarafından görüntülenir, ilanınızda yayınlanmaz.
            </p>

            <ul className="space-y-3">
              {DOCUMENT_TYPES.map((d) => (
                <li key={d.value} className="flex items-center justify-between gap-3 rounded-lg border border-ink-200 p-3">
                  <div>
                    <p className="text-sm font-medium text-ink-900">
                      {d.label} {d.required && <span className="text-red-600">*</span>}
                    </p>
                    {documents[d.value] && (
                      <p className="text-xs text-emerald-700" data-testid={`host-doc-name-${d.value}`}>
                        ✓ {documents[d.value]}
                      </p>
                    )}
                  </div>
                  <label className="yd-btn-secondary cursor-pointer shrink-0">
                    <FileUp className="h-4 w-4" aria-hidden /> Yükle
                    <input
                      type="file"
                      accept="application/pdf,image/*"
                      className="sr-only"
                      onChange={(e) => e.target.files?.[0] && void uploadDoc(d.value, e.target.files[0]!)}
                      data-testid={TID.hostDocUpload(d.value)}
                    />
                  </label>
                </li>
              ))}
            </ul>

            {/* Summary */}
            <div className="rounded-lg bg-ink-50 p-4 text-sm" data-testid="host-summary">
              <p className="font-semibold text-ink-900">Özet</p>
              <dl className="mt-2 grid gap-1 sm:grid-cols-2">
                <div className="flex justify-between gap-2"><dt className="text-ink-500">Başlık</dt><dd>{draft.title || '—'}</dd></div>
                <div className="flex justify-between gap-2"><dt className="text-ink-500">Fotoğraf</dt><dd>{photos.length}</dd></div>
                <div className="flex justify-between gap-2"><dt className="text-ink-500">Günlük</dt><dd>{formatKurus(toKurus(draft.dailyPriceLira))}</dd></div>
                <div className="flex justify-between gap-2"><dt className="text-ink-500">Hafta sonu</dt><dd>{formatKurus(toKurus(draft.weekendPriceLira))}</dd></div>
                <div className="flex justify-between gap-2"><dt className="text-ink-500">Kapasite</dt><dd>{draft.maxCapacity} kişi</dd></div>
                <div className="flex justify-between gap-2"><dt className="text-ink-500">Bahçe</dt><dd>{draft.gardenSizeM2} m²</dd></div>
                <div className="flex justify-between gap-2"><dt className="text-ink-500">Organizasyon</dt><dd>{draft.eventTypeIds.length} tip</dd></div>
                <div className="flex justify-between gap-2"><dt className="text-ink-500">Kesin çıkış</dt><dd>{draft.checkoutDeadline}</dd></div>
              </dl>
            </div>
          </section>
        )}

        {/* ---------------- Errors ---------------- */}
        {(stepErrors.length > 0 || error) && (
          <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" role="alert" data-testid="host-step-errors">
            <ul className="list-inside list-disc space-y-0.5">
              {error && <li>{error}</li>}
              {stepErrors.map((e) => <li key={e}>{e}</li>)}
            </ul>
          </div>
        )}

        {/* ---------------- Nav ---------------- */}
        <div className="mt-8 flex items-center justify-between">
          <button
            type="button"
            className="yd-btn-secondary"
            disabled={step === 1}
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            data-testid={TID.hostBack}
          >
            <ChevronLeft className="h-4 w-4" /> Geri
          </button>

          {step < 4 ? (
            <button
              type="button"
              className="yd-btn-primary"
              disabled={stepErrors.length > 0}
              onClick={() => setStep((s) => Math.min(4, s + 1))}
              data-testid={TID.hostNext}
            >
              Devam <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              className="yd-btn-primary"
              disabled={stepErrors.length > 0 || submit.isPending}
              onClick={() => submit.mutate()}
              data-testid={TID.hostSubmit}
            >
              {submit.isPending ? 'Gönderiliyor…' : 'İlanı Yayına Gönder'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
